import { spacingEvidenceText } from './spacing';
import { styleValueText } from './styleCapture.js';
import { AUTO_REVIEW_CATEGORY_TO_TICKET_TYPE } from './types/autoReview.js';
import { autoReviewNeedsVerification, autoReviewIssueDescription } from './autoReview/confidence';
import { issueTypeLabel } from './issueTypes.js';
/** Blank is absent: a blank row would render a label with nothing after it. */
function rows(...entries) {
    return entries
        .filter((e) => typeof e[1] === 'string' && e[1].trim().length > 0)
        .map(([label, value]) => ({ label, value }));
}
/** Blank selectors are real: an AI-only finding carries `selector: ''`. */
function selector(value) {
    return value && value.trim().length > 0 ? value : undefined;
}
/** A blank URL is the shape a failed upload returns, and it is not a screenshot. */
function shot(url) {
    return url && url.trim().length > 0 ? url : undefined;
}
/** Joined on '; ' rather than ', ' because a suggestion is a sentence and often contains a comma. */
function list(values) {
    const kept = (values ?? []).map((v) => v?.trim()).filter((v) => !!v);
    return kept.length > 0 ? kept.join('; ') : undefined;
}
/** Selectors and raw values are comma-joined: each is a single token, never a sentence. */
function joined(values) {
    const kept = values.map((v) => v?.trim()).filter((v) => !!v);
    return kept.length > 0 ? kept.join(', ') : undefined;
}
/**
 * A compare finding's percentage rect, rendered for a human.
 *
 * This is the only "where" a compare finding has: it anchors to a region of the
 * two IMAGES rather than to a DOM node, so dropping the rect left a reader with
 * a title and no way to find the thing at all.
 */
function region(rect) {
    if (!rect)
        return undefined;
    const r = (n) => Math.round(n);
    return `x ${r(rect.x)}%, y ${r(rect.y)}%, ${r(rect.width)}% x ${r(rect.height)}%`;
}
function pixels(rect) {
    return rect && `x ${rect.x}px, y ${rect.y}px, ${rect.width}px × ${rect.height}px`;
}
function elementDetails(element) {
    // Manual captures keep their CSS in designMetadata; scans use styleCapture.
    // Keep both shapes without adding a timestamp that the manual capture lacks.
    const css = new Map();
    const figma = [];
    for (const [key, data] of Object.entries(element?.designMetadata ?? {})) {
        if (typeof data === 'string') {
            if (data.trim())
                css.set(key, styleValueText(data));
        }
        else if (data && typeof data.value === 'string' && data.value.trim()) {
            css.set(key, styleValueText(data.value, data.cssToken || undefined));
            if (data.figmaToken)
                figma.push([`Figma token ${key}`, data.figmaToken]);
        }
    }
    // A measured scan is authoritative for a property present in both shapes.
    for (const [key, value] of Object.entries(element?.styleCapture?.values ?? {})) {
        css.set(key, styleValueText(value, element?.styleCapture?.tokens?.[key]));
    }
    return rows(['Element bounds', pixels(element?.boundingRect)], ['Styles captured at', element?.styleCapture?.capturedAt], ...[...css].map(([key, value]) => [`CSS ${key}`, value]), ...figma);
}
/** Keep each captured viewport's measurements and images together in every format. */
function autoReviewEvidenceDetails(f) {
    const evidence = f.evidence, review = f.aiReview, comparison = f.comparison;
    const details = rows(['Viewport', f.viewport && `${f.viewport.width}px × ${f.viewport.height}px`], ['Document size', f.documentDimensions ? `${f.documentDimensions.width}px × ${f.documentDimensions.height}px` : undefined], ['Measured', evidence?.detail], ['Evidence kind', evidence?.kind], ['Resource URL', evidence?.resourceUrl], ['HTTP status', evidence?.status?.toString()], ['Alignment anchor', evidence?.alignmentAnchor], ['Boundary width matches', evidence?.boundaryWidthMatches?.toString()], ['Review verdict', review?.verdict], ['Review reasoning', review?.evidence], ['Review source', review?.decisionSource], ['Design context', review?.designContext], ['Compared property', comparison?.property], ['Comparison basis', comparison?.basis], ['Comparison capture size', comparison?.documentDimensions && `${comparison.documentDimensions.width}px × ${comparison.documentDimensions.height}px`], ['Compared elements', comparison?.totalPeers?.toString()]);
    details.push(...elementDetails(f.element));
    if (comparison) {
        [comparison.target, ...comparison.peers].forEach((element, index) => {
            const label = index === 0 ? 'Finding' : `Compared element ${index}`;
            details.push(...rows([`${label} label`, element.label], [`${label} value`, element.value], [`${label} selector`, element.selector], [`${label} bounds`, pixels(element.rect)], [`${label} context bounds`, pixels(element.contextRect)]));
        });
    }
    const images = [
        ['Full-page screenshot', f.fullPageScreenshotUrl],
        ['Before screenshot', evidence?.beforeScreenshotUrl],
        ['After screenshot', evidence?.afterScreenshotUrl],
        ['Comparison screenshot', comparison?.screenshotUrl],
    ];
    details.push(...rows(...images).map(detail => ({ ...detail, image: true })));
    return details;
}
export function a11yViolationToClipboard(v, screenshotUrl) {
    const wcag = [v.wcagCriterion, v.wcagName].filter((p) => p && p.trim()).join(' ');
    return {
        title: v.title,
        // useA11yCreateIssues posts type: 'accessibility'.
        issueType: issueTypeLabel('accessibility'),
        severity: v.severity,
        description: v.description,
        selector: selector(v.element?.selector),
        screenshotUrl: shot(screenshotUrl),
        details: rows(['Category', v.category], ['WCAG', wcag], ['Found', v.found], ['Required', v.required], ['HTML', v.element?.html], ['Fix', list(v.suggestions)], ['Rule', v.axeRuleId], ['Figma', v.figmaComparison?.figmaValue], ['Live', v.figmaComparison?.liveValue], ['Source', v.source]).concat(elementDetails(v.element)),
    };
}
export function tokenFindingToClipboard(f, screenshotUrl) {
    return {
        title: f.title,
        // useTokenAuditCreateIssues posts type: 'design-token'.
        issueType: issueTypeLabel('design-token'),
        severity: f.severity,
        description: f.description,
        selector: selector(f.elements?.[0]?.selector),
        screenshotUrl: shot(screenshotUrl),
        details: rows(['Category', f.category], ['Type', f.type], ['Values', joined((f.values ?? []).map((v) => v?.raw))], ['Figma', f.figmaComparison?.figmaValue], ['Live', f.figmaComparison?.liveValue], ['Suggestion', f.suggestion], 
        // Every match, first included, so the column stands on its own in a CSV.
        ['Elements', joined((f.elements ?? []).map((e) => e?.selector))], ['Source', f.source]).concat((f.elements ?? []).flatMap((element, index) => elementDetails(element).map(detail => ({
            ...detail, label: `Element ${index + 1} · ${detail.label}`,
        })))),
    };
}
export function autoReviewFindingToClipboard(f, screenshotUrl) {
    return {
        title: f.title,
        // The SAME map useAutoReviewCreateIssues posts with, so the copy and the
        // filed issue cannot disagree: dead-ui is a Bug, visual-consistency is Debt.
        issueType: issueTypeLabel(autoReviewNeedsVerification(f) ? 'design-debt' : AUTO_REVIEW_CATEGORY_TO_TICKET_TYPE[f.category]),
        severity: autoReviewNeedsVerification(f) ? 'low' : f.severity,
        description: autoReviewIssueDescription(f),
        selector: selector(f.element?.selector),
        screenshotUrl: shot(f.screenshotUrl) ?? shot(screenshotUrl),
        details: rows(['Category', f.category], ['Check', f.checkId], ['HTML', f.element?.html], ['Source', f.source], ['Checked widths', f.observations?.map(observation => `${observation.viewport.width}px`).join(', ') || (f.viewport ? `${f.viewport.width}px` : undefined)]).concat(autoReviewEvidenceDetails(f), (f.observations ?? []).flatMap((observation, index) => {
            const prefix = `Viewport ${index + 1} (${observation.viewport.width}px × ${observation.viewport.height}px)`;
            return [
                ...rows(['Selector', observation.element?.selector], ['HTML', observation.element?.html]),
                ...rows(['Screenshot', observation.screenshotUrl]).map(detail => ({ ...detail, image: true })),
                ...autoReviewEvidenceDetails(observation),
            ].map(detail => ({ ...detail, label: `${prefix} · ${detail.label}` }));
        })),
    };
}
export function visualDifferenceToClipboard(d, screenshotUrl) {
    // Use a selector only when the captured DOM actually identified the element.
    // Image-only findings retain their regions without inventing a DOM match.
    return {
        title: d.title,
        // routes/compare.ts persists type 'design-gap'.
        issueType: issueTypeLabel('design-gap'),
        severity: d.severity,
        description: d.description,
        selector: selector(d.capturedElement?.selector),
        screenshotUrl: shot(screenshotUrl),
        details: rows(['Type', d.type], ['Measured spacing', spacingEvidenceText(d.spacing)], ['Expected (design)', d.expected], ['Actual (live)', d.actual], ['Fix', list(d.suggestions)], ['Region (live)', region(d.actualLocation ?? undefined)], ['Region (design)', region(d.location)], ['Element text', d.liveElementText ?? d.capturedElement?.fingerprint.textContent], ['Verification', d.verification], ['Measurement source', d.measured?.sourceKind], ['Shared cause', d.sharedRootCause?.statement], ['Pixel difference', d.pixelDifference?.toString()], ...(d.measured?.deltas ?? []).map(delta => [
            `Measured ${delta.property}`,
            `Expected: ${delta.expected}; Actual: ${delta.actual}; Difference: ${delta.delta}; Within tolerance: ${delta.withinTolerance}`,
        ]), ...Object.entries(d.capturedElement?.styles ?? {}).map(([property, value]) => [`Captured CSS ${property}`, styleValueText(value, d.capturedElement?.tokens?.[property])])).concat(elementDetails({ styleCapture: d.styleCapture })),
    };
}
/** 'in-progress' -> 'In Progress', the bulk menus' Set Status label. Also the status line of a server-rendered copy (server lib/issueCopyText.ts). */
export function issueStatusLabel(status) {
    if (!status)
        return undefined;
    return status.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
/** Copy for saved issues (2026-10-20): the issue lists' Copy category, extension and team review. */
export function savedIssueToClipboard(issue) {
    const browser = [issue.browserName, issue.browserVersion].filter((p) => p && p.trim()).join(' ');
    const viewport = issue.viewportWidth && issue.viewportHeight ? `${issue.viewportWidth}×${issue.viewportHeight}` : undefined;
    return {
        title: issue.title,
        issueType: issueTypeLabel(issue.type),
        severity: issue.severity ?? undefined,
        description: issue.description ?? undefined,
        selector: selector(issue.elementData?.selector),
        screenshotUrl: shot(issue.screenshotUrl ?? undefined),
        pageUrl: issue.pageUrl ?? undefined,
        details: rows(['ID', issue.displayId ?? undefined], ['Status', issueStatusLabel(issue.status)], ['Tags', joined((issue.labels ?? []).map((l) => l?.name))], ['Element', issue.elementData?.tagName?.toLowerCase()], ['Alt text', issue.elementData?.altText], ['Browser', browser], ['OS', issue.os ?? undefined], ['Viewport', viewport], ['Figma frame', issue.figmaFrameUrl ?? undefined]).concat(
        // CSV is the local table (text and Markdown render with the export
        // formatter, 2026-10-28), so it carries the bounds and captured CSS too.
        elementDetails(issue.elementData ?? undefined), rows(['Full-page screenshot', issue.fullPageScreenshotUrl ?? undefined]).map((d) => ({ ...d, image: true }))),
    };
}
//# sourceMappingURL=findingsClipboardMappers.js.map